import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, Library, Loader2, Flag, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const newId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `d_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
);

/**
 * Two-step dialog that lets a user pick a past estimate (filtered by the
 * current implementation type + complexity) and bulk-import its
 * milestone-flagged deliverables into the in-progress estimate's
 * componentDeliverables map.
 *
 * Bullets are merged additively per component; bullets that already exist
 * (case-insensitive text match) are skipped automatically. Components in
 * the source estimate that are not currently selected on this estimate
 * are listed but disabled so the user can see them but not import them.
 *
 * Props:
 *   open                    boolean
 *   onClose()
 *   implementationType      string (current estimate)
 *   complexity              string (current estimate)
 *   selectedComponentNames  string[] (currently selected on this estimate)
 *   currentDeliverables     { [componentName]: [{id,text,is_milestone}] }
 *   onImport(nextMap)       called with the merged map
 */
export function CloneScopeSkeletonDialog({
  open,
  onClose,
  implementationType,
  complexity,
  selectedComponentNames,
  currentDeliverables,
  onImport,
}) {
  const [view, setView] = useState('list'); // 'list' | 'preview'
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null); // { id, estimate_name, components }
  const [pickedLoading, setPickedLoading] = useState(false);
  const [checked, setChecked] = useState({}); // { 'compName||text': true }

  const selectedSet = useMemo(
    () => new Set(selectedComponentNames || []),
    [selectedComponentNames]
  );

  // Reset whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setView('list');
    setPicked(null);
    setChecked({});
    setLoading(true);
    axios.get(`${API_URL}/api/deliverables/similar-estimates`, {
      params: { implementation_type: implementationType, complexity, limit: 10 },
      withCredentials: true,
    })
      .then(res => setEstimates(res.data?.estimates || []))
      .catch(() => setEstimates([]))
      .finally(() => setLoading(false));
  }, [open, implementationType, complexity]);

  const openPreview = (estimateId) => {
    setPickedLoading(true);
    setView('preview');
    axios.get(`${API_URL}/api/deliverables/from-estimate/${estimateId}`, { withCredentials: true })
      .then(res => {
        setPicked(res.data || null);
        // Pre-check every milestone bullet that maps to a currently-selected component
        const next = {};
        for (const grp of (res.data?.components || [])) {
          if (!selectedSet.has(grp.component_name)) continue;
          for (const b of (grp.deliverables || [])) {
            next[`${grp.component_name}||${b.text.toLowerCase()}`] = true;
          }
        }
        setChecked(next);
      })
      .catch(() => { setPicked(null); toast.error('Could not load that estimate'); })
      .finally(() => setPickedLoading(false));
  };

  const toggleBullet = (compName, text) => {
    const key = `${compName}||${text.toLowerCase()}`;
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const importedCount = Object.values(checked).filter(Boolean).length;

  const handleImport = () => {
    if (!picked) return;
    const merged = { ...currentDeliverables };
    let importedBullets = 0;
    let skippedDuplicates = 0;
    for (const grp of (picked.components || [])) {
      const compName = grp.component_name;
      if (!selectedSet.has(compName)) continue;
      const existing = merged[compName] ? [...merged[compName]] : [];
      const existingTexts = new Set(existing.map(b => (b.text || '').trim().toLowerCase()));
      for (const b of (grp.deliverables || [])) {
        const key = `${compName}||${b.text.toLowerCase()}`;
        if (!checked[key]) continue;
        const norm = b.text.trim().toLowerCase();
        if (existingTexts.has(norm)) {
          skippedDuplicates += 1;
          continue;
        }
        existing.push({ id: newId(), text: b.text, is_milestone: true });
        existingTexts.add(norm);
        importedBullets += 1;
      }
      if (existing.length > 0) merged[compName] = existing;
    }
    onImport(merged);
    if (importedBullets === 0) {
      toast.message('Nothing imported — all selected bullets were already in your scope.');
    } else {
      const tail = skippedDuplicates > 0 ? ` (${skippedDuplicates} duplicate${skippedDuplicates === 1 ? '' : 's'} skipped)` : '';
      toast.success(`Imported ${importedBullets} milestone${importedBullets === 1 ? '' : 's'} from "${picked.estimate_name}"${tail}`);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="clone-skeleton-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <Library className="w-4 h-4 text-cyan-600" />
            {view === 'list' ? 'Clone scope skeleton from a past estimate' : `Preview milestones — ${picked?.estimate_name || ''}`}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {view === 'list' ? (
              <>
                Pick a past estimate of the same type to bulk-import its
                milestone-flagged deliverables into your current scope. Showing
                matches for <strong>{implementationType}</strong> ·{' '}
                <strong>{complexity}</strong>.
              </>
            ) : (
              <>Choose which milestone bullets to copy. Bullets for components you haven&apos;t selected on this estimate are shown but cannot be imported.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {view === 'list' && (
          <div className="space-y-2 mt-2">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
              </div>
            )}
            {!loading && estimates.length === 0 && (
              <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md py-8 text-center">
                No past estimates with milestone deliverables match this implementation type and complexity.
              </div>
            )}
            {!loading && estimates.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => openPreview(e.id)}
                className="w-full text-left p-3 rounded-md border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 transition-colors group"
                data-testid={`clone-skeleton-estimate-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0F2040] truncate">{e.estimate_name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {e.organization_name && <>{e.organization_name} · </>}
                      {e.implementation_type} · {e.complexity}
                      {e.created_at && <> · {new Date(e.created_at).toLocaleDateString()}</>}
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      {(e.components_with_milestones || []).slice(0, 4).map(c => (
                        <Badge
                          key={c.component_name}
                          variant="outline"
                          className="text-[10px] font-normal border-slate-200 text-slate-600"
                        >
                          {c.component_name} ({c.milestone_count})
                        </Badge>
                      ))}
                      {(e.components_with_milestones || []).length > 4 && (
                        <span className="text-[10px] text-slate-400">+{e.components_with_milestones.length - 4} more</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                      <Flag className="w-3 h-3" />
                      {e.milestone_count}
                    </div>
                    <div className="text-[10px] text-slate-400">milestone{e.milestone_count === 1 ? '' : 's'}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {view === 'preview' && (
          <div className="space-y-2 mt-2">
            {pickedLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
              </div>
            )}
            {!pickedLoading && picked && picked.components.length === 0 && (
              <div className="text-xs text-slate-400 italic py-6 text-center">
                That estimate has no milestone bullets.
              </div>
            )}
            {!pickedLoading && picked && picked.components.map((grp) => {
              const isImportable = selectedSet.has(grp.component_name);
              return (
                <div
                  key={grp.component_name}
                  className={`border rounded-md p-2 ${isImportable ? 'border-slate-200' : 'border-slate-100 bg-slate-50/40 opacity-70'}`}
                  data-testid={`clone-skeleton-group-${grp.component_name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-semibold text-[#0F2040]">{grp.component_name}</div>
                    {!isImportable && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                        <AlertCircle className="w-3 h-3" />
                        Not selected on this estimate
                      </span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {grp.deliverables.map((b) => {
                      const key = `${grp.component_name}||${b.text.toLowerCase()}`;
                      const isOn = !!checked[key];
                      return (
                        <li key={key}>
                          <label
                            className={`flex items-start gap-2 text-xs ${isImportable ? 'cursor-pointer hover:bg-cyan-50/40' : 'cursor-not-allowed'} px-1.5 py-1 rounded`}
                          >
                            <input
                              type="checkbox"
                              checked={isOn}
                              disabled={!isImportable}
                              onChange={() => toggleBullet(grp.component_name, b.text)}
                              className="w-3.5 h-3.5 mt-0.5 accent-[#0F2040]"
                              data-testid={`clone-skeleton-bullet-${grp.component_name.replace(/\s+/g, '-').toLowerCase()}-${grp.deliverables.indexOf(b)}`}
                            />
                            <Flag className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                            <span className="text-slate-700 leading-snug">{b.text}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-3 flex-row items-center justify-between sm:justify-between">
          <div className="flex items-center gap-2">
            {view === 'preview' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setView('list')}
                data-testid="clone-skeleton-back-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                Back
              </Button>
            )}
            {view === 'preview' && (
              <span className="text-[11px] text-slate-500">
                {importedCount} bullet{importedCount === 1 ? '' : 's'} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              data-testid="clone-skeleton-cancel-btn"
            >
              Cancel
            </Button>
            {view === 'preview' && (
              <Button
                type="button"
                size="sm"
                onClick={handleImport}
                disabled={importedCount === 0}
                className="bg-[#0F2040] hover:bg-[#0F2040]/90"
                data-testid="clone-skeleton-import-btn"
              >
                <Check className="w-3.5 h-3.5 mr-1" />
                Import {importedCount > 0 ? importedCount : ''} milestone{importedCount === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CloneScopeSkeletonDialog;
